import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { UserPlusIcon, CheckCircleIcon, LanguageIcon, SunIcon, MoonIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { addDoc, collection } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import Logo from '../icons/Logo';

interface CustomerRegistrationFormProps {
  organizationId: string;
  onSuccess?: () => void;
}

interface FormData {
  customerType: 'individual' | 'organization';
  // Individual fields
  firstName: string;
  lastName: string;
  // Organization fields
  companyName: string;
  taxNumber: string;
  registrationNumber: string;
  // Common fields
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  // Contact person (for organizations)
  contactPersonName: string;
  contactPersonEmail: string;
  contactPersonPhone: string;
  contactPersonPosition: string;
  contactPersonFax: string;
  // Financial info
  bankAccount: string;
  message: string;
}

const CustomerRegistrationForm: React.FC<CustomerRegistrationFormProps> = ({
  organizationId,
  onSuccess
}) => {
  const { t, language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [formData, setFormData] = useState<FormData>({
    customerType: 'individual',
    firstName: '',
    lastName: '',
    companyName: '',
    taxNumber: '',
    registrationNumber: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'Magyarország',
    contactPersonName: '',
    contactPersonEmail: '',
    contactPersonPhone: '',
    contactPersonPosition: '',
    contactPersonFax: '',
    bankAccount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (field: keyof FormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validate required fields for organization
    if (formData.customerType === 'organization') {
      if (!formData.companyName.trim()) {
        setError('Cégnév megadása kötelező');
        setIsSubmitting(false);
        return;
      }
      if (!formData.taxNumber.trim()) {
        setError('Adószám megadása kötelező');
        setIsSubmitting(false);
        return;
      }
      if (!formData.registrationNumber.trim()) {
        setError('Cégjegyzékszám megadása kötelező');
        setIsSubmitting(false);
        return;
      }
      if (!formData.city.trim()) {
        setError('Város megadása kötelező');
        setIsSubmitting(false);
        return;
      }
      if (!formData.postalCode.trim()) {
        setError('Irányítószám megadása kötelező');
        setIsSubmitting(false);
        return;
      }
      if (!formData.contactPersonName.trim()) {
        setError('Kapcsolattartó neve kötelező');
        setIsSubmitting(false);
        return;
      }
      if (!formData.contactPersonPhone.trim()) {
        setError('Kapcsolattartó telefonszáma kötelező');
        setIsSubmitting(false);
        return;
      }
    }

    // Validate required fields for individual
    if (formData.customerType === 'individual') {
      if (!formData.firstName.trim()) {
        setError('Keresztnév megadása kötelező');
        setIsSubmitting(false);
        return;
      }
      if (!formData.lastName.trim()) {
        setError('Vezetéknév megadása kötelező');
        setIsSubmitting(false);
        return;
      }
    }

    // Email is always required
    if (!formData.email.trim()) {
      setError('Email cím megadása kötelező');
      setIsSubmitting(false);
      return;
    }

    try {
      // Ügyfél létrehozása
      const customerData = {
        type: formData.customerType,
        // Individual fields
        firstName: formData.customerType === 'individual' ? formData.firstName : '',
        lastName: formData.customerType === 'individual' ? formData.lastName : '',
        // Organization fields  
        companyName: formData.customerType === 'organization' ? formData.companyName : '',
        taxNumber: formData.customerType === 'organization' ? formData.taxNumber : '',
        registrationNumber: formData.customerType === 'organization' ? formData.registrationNumber : '',
        // Common fields
        email: formData.email,
        phone: formData.phone,
        address: {
          street: formData.address,
          city: formData.city,
          postalCode: formData.postalCode,
          country: formData.country
        },
        // Contact person (for organizations)
        contactPerson: formData.customerType === 'organization' ? {
          name: formData.contactPersonName,
          email: formData.contactPersonEmail,
          phone: formData.contactPersonPhone,
          position: formData.contactPersonPosition,
          fax: formData.contactPersonFax
        } : {
          name: '',
          email: '',
          phone: '',
          position: '',
          fax: ''
        },
        notes: formData.message,
        organizationId,
        organizations: [organizationId],
        source: 'public_registration_form',
        status: 'new',
        registrationSource: 'public_link',
        isActive: true,
        documents: [],
        bankAccount: formData.customerType === 'organization' ? formData.bankAccount : '',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'customers'), customerData);

      // Sikeres regisztráció
      setIsSuccess(true);
      onSuccess?.();

      // Email értesítés küldése (opcionális)
      // TODO: Firebase Function trigger az email küldéshez

    } catch (error) {
      console.error('Error creating customer:', error);
      setError('Hiba történt a regisztráció során. Kérjük, próbálja meg újra.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen relative bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
        {/* Professional minimal background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
            <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
            }} />
          </div>
        </div>

        <div className="relative max-w-md w-full m-4">
          <div className="bg-white dark:bg-gray-800 bg-opacity-95 dark:bg-opacity-95 backdrop-blur-sm p-8 rounded-2xl shadow-xl text-center">
            <Logo className="h-16 mx-auto mb-6" />
            <CheckCircleIcon className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              {t('customers.registration.successTitle') || 'Sikeres regisztráció!'}
            </h2>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              {t('customers.registration.successMessage') || 'Köszönjük regisztrációját! Hamarosan felvesszük Önnel a kapcsolatot.'}
            </p>
            {t('customers.registration.confirmationEmail') && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('customers.registration.confirmationEmail')}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative bg-gray-100 dark:bg-gray-900 flex items-center justify-center overflow-hidden">
      {/* Theme and Language Selectors */}
      <div className="absolute top-4 right-4 z-20 flex items-center space-x-3">
        {/* Theme Selector */}
        <button
          onClick={toggleTheme}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md"
          aria-label={theme === 'light' ? 'Váltás sötét módra' : 'Váltás világos módra'}
        >
          {theme === 'light' ? (
            <MoonIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          ) : (
            <SunIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          )}
        </button>

        {/* Language Selector */}
        <button
          onClick={() => setLanguage(language === 'hu' ? 'en' : 'hu')}
          className="p-2 rounded-lg bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-md flex items-center space-x-1"
          aria-label={language === 'hu' ? 'Switch to English' : 'Váltás magyarra'}
        >
          <GlobeAltIcon className="h-5 w-5 text-gray-700 dark:text-gray-300" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 uppercase">
            {language === 'hu' ? 'EN' : 'HU'}
          </span>
        </button>
      </div>

      {/* Professional minimal background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
          <div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Ccircle cx='7' cy='7' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }} />
        </div>
      </div>

      {/* Registration container */}
      <div className="relative max-w-2xl w-full m-4">
        <div className="bg-white dark:bg-gray-800 bg-opacity-95 dark:bg-opacity-95 backdrop-blur-sm p-8 rounded-2xl shadow-xl">
          <div className="text-center mb-8">
            <Logo className="h-16 mx-auto mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              {t('customers.registration.title') || 'Ügyfél Regisztráció'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              {t('customers.registration.subtitle') || 'Töltse ki az alábbi űrlapot, és hamarosan felvesszük Önnel a kapcsolatot.'}
            </p>
          </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              {t('customers.registration.fields.customerType') || 'Ügyfél típusa'}
            </label>
            <div className="flex gap-4">
              <label className="relative flex items-center group flex-1">
                <input
                  type="radio"
                  value="individual"
                  checked={formData.customerType === 'individual'}
                  onChange={handleInputChange('customerType')}
                  className="sr-only peer"
                />
                <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                              peer-checked:border-gray-500 peer-checked:bg-gray-50 dark:peer-checked:bg-gray-700
                              hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200">
                  <UserPlusIcon className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('customers.registration.types.individual') || 'Magánszemély'}
                  </span>
                </div>
              </label>
              <label className="relative flex items-center group flex-1">
                <input
                  type="radio"
                  value="organization"
                  checked={formData.customerType === 'organization'}
                  onChange={handleInputChange('customerType')}
                  className="sr-only peer"
                />
                <div className="flex items-center justify-center w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 
                              peer-checked:border-gray-500 peer-checked:bg-gray-50 dark:peer-checked:bg-gray-700
                              hover:border-gray-400 dark:hover:border-gray-500 cursor-pointer transition-all duration-200">
                  <UserPlusIcon className="w-4 h-4 mr-2 text-gray-600 dark:text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    {t('customers.registration.types.organization') || 'Cég'}
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Dynamic fields based on customer type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {formData.customerType === 'individual' ? (
              // Individual fields
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.firstName') || 'Keresztnév'} *
                  </label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={handleInputChange('firstName')}
                    placeholder={t('customers.registration.placeholders.firstName') || 'Keresztnév'}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.lastName') || 'Vezetéknév'} *
                  </label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={handleInputChange('lastName')}
                    placeholder={t('customers.registration.placeholders.lastName') || 'Vezetéknév'}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </>
            ) : (
              // Organization fields
              <>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.companyName') || 'Cégnév'} *
                  </label>
                  <input
                    type="text"
                    value={formData.companyName}
                    onChange={handleInputChange('companyName')}
                    placeholder={t('customers.registration.placeholders.companyName') || 'Cég neve'}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.taxNumber') || 'Adószám'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.taxNumber}
                    onChange={handleInputChange('taxNumber')}
                    placeholder={t('customers.registration.placeholders.taxNumber') || '12345678-1-23'}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.registrationNumber') || 'Cégjegyzékszám'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.registrationNumber}
                    onChange={handleInputChange('registrationNumber')}
                    placeholder={t('customers.registration.placeholders.registrationNumber') || '01-09-123456'}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.contactPersonName') || 'Kapcsolattartó neve'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.contactPersonName}
                    onChange={handleInputChange('contactPersonName')}
                    placeholder={t('customers.registration.placeholders.contactPersonName') || 'Kapcsolattartó teljes neve'}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.contactPersonPosition') || 'Beosztás'}
                  </label>
                  <input
                    type="text"
                    value={formData.contactPersonPosition}
                    onChange={handleInputChange('contactPersonPosition')}
                    placeholder={t('customers.registration.placeholders.contactPersonPosition') || 'pl. Ügyvezető'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.contactPersonEmail') || 'Kapcsolattartó email'}
                  </label>
                  <input
                    type="email"
                    value={formData.contactPersonEmail}
                    onChange={handleInputChange('contactPersonEmail')}
                    placeholder={t('customers.registration.placeholders.contactPersonEmail') || 'kapcsolattarto@ceg.hu'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.contactPersonPhone') || 'Kapcsolattartó telefon'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPersonPhone}
                    onChange={handleInputChange('contactPersonPhone')}
                    placeholder={t('customers.registration.placeholders.contactPersonPhone') || '+36 30 123 4567'}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.contactPersonFax') || 'Fax'}
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPersonFax}
                    onChange={handleInputChange('contactPersonFax')}
                    placeholder={t('customers.registration.placeholders.contactPersonFax') || '+36 1 234 5678'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('customers.registration.fields.bankAccount') || 'Bankszámlaszám'}
                  </label>
                  <input
                    type="text"
                    value={formData.bankAccount}
                    onChange={handleInputChange('bankAccount')}
                    placeholder={t('customers.registration.placeholders.bankAccount') || '12345678-12345678-12345678'}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {formData.customerType === 'organization' 
                  ? (t('customers.registration.fields.companyEmail') || 'Cég e-mail címe')
                  : (t('customers.registration.fields.email') || 'Email cím')
                } <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                placeholder={t('customers.registration.placeholders.email') || 'email@example.com'}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.registration.fields.phone') || 'Telefonszám'} *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                placeholder={t('customers.registration.placeholders.phone') || '+36 30 123 4567'}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>


            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.registration.fields.address') || 'Cím'}
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={handleInputChange('address')}
                placeholder={t('customers.registration.placeholders.address') || 'Utca, házszám'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.registration.fields.city') || 'Város'} {formData.customerType === 'organization' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={handleInputChange('city')}
                placeholder={t('customers.registration.placeholders.city') || 'Város'}
                required={formData.customerType === 'organization'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.registration.fields.postalCode') || 'Irányítószám'} {formData.customerType === 'organization' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={formData.postalCode}
                onChange={handleInputChange('postalCode')}
                placeholder={t('customers.registration.placeholders.postalCode') || '1234'}
                required={formData.customerType === 'organization'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.registration.fields.country') || 'Ország'}
              </label>
              <input
                type="text"
                value={formData.country}
                onChange={handleInputChange('country')}
                placeholder={t('customers.registration.placeholders.country') || 'Magyarország'}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('customers.registration.fields.message') || 'Üzenet / Megjegyzés'}
              </label>
              <textarea
                value={formData.message}
                onChange={handleInputChange('message')}
                placeholder={t('customers.registration.placeholders.message') || 'Írja le röviden, miben segíthetünk...'}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-500 dark:bg-gray-700 dark:text-gray-100"
              />
            </div>
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-xl text-white bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-800 transform transition duration-200 ease-in-out hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('customers.registration.submitting') || 'Küldés...'}
              </div>
            ) : (
              <>
                <UserPlusIcon className="h-5 w-5 mr-2" />
                {t('customers.registration.submit') || 'Regisztráció küldése'}
              </>
            )}
          </button>

          <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">
            {t('customers.registration.privacy') || 'A regisztrációval elfogadja az'}{' '}
            <a 
              href="https://youcrm.hu/docs" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 underline transition-colors"
            >
              {t('customers.registration.privacyLink') || 'adatvédelmi tájékoztatónkat'}
            </a>
            {t('customers.registration.privacyEnd') || '.'}
          </p>
        </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerRegistrationForm;
